import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Upload, X } from 'lucide-react';

const INTAKE_OPTIONS = [
  'Psychological Assessment', 'Developmental Assessment', 'Neuropsychological Assessment',
  'Neurofeedback/Brain Mapping Assessment', 'Psychiatric Assessment',
  'Individual Therapy (Child/Toddler/Adolescent/Adult)', 'Couples Therapy', 'Marriage Therapy',
  'ABA Therapy', 'Neurofeedback (Ongoing)', 'Parent Management Training',
  'Group Therapy (various)', 'Executive Functioning Treatment', 'Feelings App Consultation',
  'Forensic (Custody) Assessment', 'Test Preparation', 'Clinician Training (Autism/Billing)', 'Other',
];

const REFERRAL_OPTIONS = [
  'Parent', 'Primary Doctor', 'NYC DOE', 'Word of Mouth', 'Grace Baptist Church',
  'Dole Center', 'S.W.I.M.', 'Intellectus Prep Charter School', 'Social Media', 'Other',
];

const STEPS = [
  { num: 1, label: 'Availability' },
  { num: 2, label: 'Patient Info' },
  { num: 3, label: 'Insurance' },
  { num: 4, label: 'Intake' },
  { num: 5, label: 'Documents' },
  { num: 6, label: 'Notes' },
];

const inputCls = "w-full px-4 py-3 rounded-xl border border-[var(--border-color)] bg-[var(--bg-alt)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-blue)] text-sm";

export default function BookEvaluation() {
  const { apiClient } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    availability: [], availability_other: '',
    patient_name: '', patient_dob: '', patient_gender: '', guardian_name: '', legal_address: '', phone: '', email: '',
    insured_name: '', insured_dob: '', insured_gender: '',
    intake_reason: '', referral_source: '',
    insurance_front_path: null, insurance_back_path: null,
    insurance_front_name: '', insurance_back_name: '',
    additional_notes: '',
  });

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const toggleAvail = (val) => {
    setForm(prev => ({
      ...prev,
      availability: prev.availability.includes(val)
        ? prev.availability.filter(v => v !== val)
        : [...prev.availability, val],
    }));
  };

  const uploadFile = useCallback(async (file, field, nameField) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await apiClient.post('/api/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, [field]: data.path, [nameField]: file.name }));
      toast.success(`${file.name} uploaded`);
    } catch {
      toast.error('Upload failed');
    }
  }, [apiClient]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await apiClient.post('/api/bookings', {
        availability: form.availability, availability_other: form.availability_other,
        patient_name: form.patient_name, patient_dob: form.patient_dob, patient_gender: form.patient_gender,
        guardian_name: form.guardian_name, legal_address: form.legal_address, phone: form.phone, email: form.email,
        insured_name: form.insured_name, insured_dob: form.insured_dob, insured_gender: form.insured_gender,
        intake_reason: form.intake_reason, referral_source: form.referral_source,
        insurance_front_path: form.insurance_front_path, insurance_back_path: form.insurance_back_path,
        additional_notes: form.additional_notes,
      });
      toast.success('Booking submitted successfully!');
      navigate('/dashboard');
    } catch {
      toast.error('Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-16 bg-[var(--bg-alt)]" data-testid="book-evaluation-page">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[var(--primary-blue)] font-semibold mb-6 hover:gap-3 transition-all">
          <ArrowLeft size={16} /> Back to Dashboard
        </Link>
        <h2 className="text-3xl font-heading font-bold mb-2">Book an Evaluation</h2>
        <p className="text-[var(--text-body)] mb-8">Complete all sections to schedule your evaluation</p>

        {/* Progress */}
        <div className="flex items-center justify-between mb-10 relative overflow-x-auto pb-2" data-testid="progress-tracker">
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-[var(--border-color)]" />
          <motion.div className="absolute top-5 left-0 h-0.5 bg-[var(--primary-blue)]" initial={{ width: '0%' }} animate={{ width: `${((step - 1) / (STEPS.length - 1)) * 100}%` }} transition={{ duration: 0.4 }} />
          {STEPS.map(s => (
            <div key={s.num} className="relative z-10 flex flex-col items-center min-w-[60px]">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold text-sm transition-colors ${step > s.num ? 'bg-[var(--primary-blue)] text-white' : step === s.num ? 'bg-[var(--primary-blue)] text-white ring-4 ring-blue-100' : 'bg-white border-2 border-[var(--border-color)] text-[var(--text-body)]'}`}>
                {step > s.num ? <Check size={16} /> : s.num}
              </div>
              <span className={`text-[10px] mt-1.5 font-semibold whitespace-nowrap ${step >= s.num ? 'text-[var(--primary-blue)]' : 'text-[var(--text-body)]'}`}>{s.label}</span>
            </div>
          ))}
        </div>

        <div className="card-elevated p-8">
          {/* Step 1: Availability */}
          {step === 1 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5" data-testid="step-1">
              <h3 className="text-xl font-heading font-bold">General Availability</h3>
              <p className="text-sm text-[var(--text-body)]">Select all times that work for you</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {['Mornings', 'Afternoons', 'Evenings', 'Weekends', 'Flexible'].map(opt => (
                  <label key={opt} className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-all ${form.availability.includes(opt) ? 'border-[var(--primary-blue)] bg-blue-50' : 'border-[var(--border-color)] hover:border-[var(--primary-blue)]/50'}`}>
                    <input type="checkbox" checked={form.availability.includes(opt)} onChange={() => toggleAvail(opt)} className="w-4 h-4 rounded" />
                    <span className="text-sm font-medium">{opt}</span>
                  </label>
                ))}
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5">Other (specify)</label>
                <input value={form.availability_other} onChange={e => set('availability_other', e.target.value)} className={inputCls} placeholder="Any other availability details" data-testid="availability-other" />
              </div>
              <button onClick={() => setStep(2)} className="w-full btn-primary" data-testid="next-step-1">Next: Patient Information</button>
            </motion.div>
          )}

          {/* Step 2: Patient Info */}
          {step === 2 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5" data-testid="step-2">
              <h3 className="text-xl font-heading font-bold">Patient Information</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold mb-1.5">Full Name of Patient *</label>
                  <input value={form.patient_name} onChange={e => set('patient_name', e.target.value)} required className={inputCls} data-testid="patient-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1.5">Date of Birth *</label>
                  <input type="date" value={form.patient_dob} onChange={e => set('patient_dob', e.target.value)} required className={inputCls} data-testid="patient-dob-input" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5">Gender at Birth *</label>
                <div className="flex gap-6">
                  {['Male', 'Female'].map(g => (
                    <label key={g} className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="patient_gender" value={g} checked={form.patient_gender === g} onChange={e => set('patient_gender', e.target.value)} className="w-4 h-4" />
                      <span className="text-sm">{g}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5">Legal Name of Guardian (if minor)</label>
                <input value={form.guardian_name} onChange={e => set('guardian_name', e.target.value)} className={inputCls} data-testid="guardian-name-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5">Legal Address *</label>
                <textarea value={form.legal_address} onChange={e => set('legal_address', e.target.value)} required rows="2" className={inputCls + " resize-none"} data-testid="legal-address-input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold mb-1.5">Phone Number *</label>
                  <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} required className={inputCls} data-testid="phone-input" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1.5">Email Address *</label>
                  <input type="email" value={form.email} onChange={e => set('email', e.target.value)} required className={inputCls} data-testid="email-input" />
                </div>
              </div>
              <div className="flex gap-4">
                <button onClick={() => setStep(1)} className="flex-1 btn-outline">Back</button>
                <button onClick={() => setStep(3)} className="flex-1 btn-primary" data-testid="next-step-2">Next: Insurance</button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Insurance */}
          {step === 3 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5" data-testid="step-3">
              <h3 className="text-xl font-heading font-bold">Insurance / Payer Information</h3>
              <div>
                <label className="block text-sm font-semibold mb-1.5">Primary Insured Name *</label>
                <input value={form.insured_name} onChange={e => set('insured_name', e.target.value)} required className={inputCls} data-testid="insured-name-input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold mb-1.5">Insured Date of Birth *</label>
                  <input type="date" value={form.insured_dob} onChange={e => set('insured_dob', e.target.value)} required className={inputCls} data-testid="insured-dob-input" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1.5">Insured Gender *</label>
                  <div className="flex gap-6 pt-3">
                    {['Male', 'Female'].map(g => (
                      <label key={g} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="insured_gender" value={g} checked={form.insured_gender === g} onChange={e => set('insured_gender', e.target.value)} className="w-4 h-4" />
                        <span className="text-sm">{g}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex gap-4">
                <button onClick={() => setStep(2)} className="flex-1 btn-outline">Back</button>
                <button onClick={() => setStep(4)} className="flex-1 btn-primary" data-testid="next-step-3">Next: Intake & Referral</button>
              </div>
            </motion.div>
          )}

          {/* Step 4: Intake & Referral */}
          {step === 4 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5" data-testid="step-4">
              <h3 className="text-xl font-heading font-bold">Intake & Referral</h3>
              <div>
                <label className="block text-sm font-semibold mb-1.5">Intake Reason *</label>
                <select value={form.intake_reason} onChange={e => set('intake_reason', e.target.value)} required className={inputCls} data-testid="intake-reason-select">
                  <option value="">Select reason...</option>
                  {INTAKE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5">Referral Source *</label>
                <select value={form.referral_source} onChange={e => set('referral_source', e.target.value)} required className={inputCls} data-testid="referral-source-select">
                  <option value="">Select source...</option>
                  {REFERRAL_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="flex gap-4">
                <button onClick={() => setStep(3)} className="flex-1 btn-outline">Back</button>
                <button onClick={() => setStep(5)} className="flex-1 btn-primary" data-testid="next-step-4">Next: Documents</button>
              </div>
            </motion.div>
          )}

          {/* Step 5: Documents */}
          {step === 5 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5" data-testid="step-5">
              <h3 className="text-xl font-heading font-bold">Document Uploads</h3>
              <p className="text-sm text-[var(--text-body)]">Upload front and back of insurance card or AA2 form (PDF, DOC, or Image, max 10MB)</p>
              {[
                { label: 'Front of Insurance Card / AA2 Form', field: 'insurance_front_path', nameField: 'insurance_front_name' },
                { label: 'Back of Insurance Card / AA2 Form', field: 'insurance_back_path', nameField: 'insurance_back_name' },
              ].map(item => (
                <div key={item.field}>
                  <label className="block text-sm font-semibold mb-1.5">{item.label} *</label>
                  {form[item.field] ? (
                    <div className="flex items-center gap-3 p-3 rounded-xl border border-green-200 bg-green-50">
                      <Check size={16} className="text-green-600" />
                      <span className="text-sm text-green-700 flex-1">{form[item.nameField]}</span>
                      <button onClick={() => setForm(prev => ({ ...prev, [item.field]: null, [item.nameField]: '' }))} className="text-red-400 hover:text-red-600"><X size={14} /></button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center gap-2 p-6 rounded-xl border-2 border-dashed border-[var(--border-color)] hover:border-[var(--primary-blue)] cursor-pointer transition-colors">
                      <Upload size={24} className="text-[var(--text-body)]" />
                      <span className="text-sm text-[var(--text-body)]">Click to upload</span>
                      <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" onChange={e => { if (e.target.files[0]) uploadFile(e.target.files[0], item.field, item.nameField); }} data-testid={`upload-${item.field}`} />
                    </label>
                  )}
                </div>
              ))}
              <div className="flex gap-4">
                <button onClick={() => setStep(4)} className="flex-1 btn-outline">Back</button>
                <button onClick={() => setStep(6)} className="flex-1 btn-primary" data-testid="next-step-5">Next: Additional Notes</button>
              </div>
            </motion.div>
          )}

          {/* Step 6: Notes + Submit */}
          {step === 6 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5" data-testid="step-6">
              <h3 className="text-xl font-heading font-bold">Additional Information</h3>
              <div>
                <label className="block text-sm font-semibold mb-1.5">Additional Notes (Optional)</label>
                <textarea value={form.additional_notes} onChange={e => set('additional_notes', e.target.value)} rows="4" className={inputCls + " resize-none"} placeholder="Any other information you'd like us to know..." data-testid="additional-notes-input" />
              </div>
              <div className="flex gap-4">
                <button onClick={() => setStep(5)} className="flex-1 btn-outline">Back</button>
                <button onClick={handleSubmit} disabled={loading} className="flex-1 btn-primary disabled:opacity-50" data-testid="submit-booking-btn">
                  {loading ? 'Submitting...' : 'Submit Evaluation Request'}
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
